// @phase13-shim — replaces 466-line legacy body after P5 cutover.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DesignerShellPage } from '../../designer';
import { useSceneStore } from '../../store/sceneStore';
import VersionHistoryPanel from '../../components/versioning/VersionHistoryPanel';
import { coachAPI, sceneAPI } from '../../services/api';
import ProjectSceneForm from '../../components/scenes/ProjectSceneForm';
import FlashcardSceneEditor from './FlashcardSceneEditorPage';

// ─── ov-4: Checkpoint editor for content_panel scenes ─────────────────────
// Stores MCQ checkpoints in scene.domain_metadata.content_mcq_markers[].
// Schema mirrors video mcq_markers but has no timestamp_ms (positional only).
function ContentPanelCheckpointEditor({ sceneId }) {
  const scene = useSceneStore((s) => s.scene);
  const updateScene = useSceneStore((s) => s.updateScene);
  const [showForm, setShowForm] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null); // null = add new
  const [formState, setFormState] = useState(null);

  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const markers = meta.content_mcq_markers || [];

  const openAddForm = () => {
    setEditingIdx(null);
    setFormState({
      prompt: '',
      choices: [
        { choice_id: 'a', text: '', correct: true },
        { choice_id: 'b', text: '', correct: false },
        { choice_id: 'c', text: '', correct: false },
        { choice_id: 'd', text: '', correct: false },
      ],
      max_attempts: 2,
    });
    setShowForm(true);
  };

  const openEditForm = (idx) => {
    const m = markers[idx];
    setEditingIdx(idx);
    setFormState({
      prompt: m.question?.prompt || '',
      choices: (m.question?.choices || []).map((c) => ({ ...c })),
      max_attempts: m.max_attempts || 2,
    });
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setFormState(null); setEditingIdx(null); };

  const saveMarker = () => {
    if (!formState?.prompt?.trim()) return;
    const markerId = editingIdx != null
      ? markers[editingIdx].marker_id
      : `cpanel_${sceneId}_${Date.now().toString(36)}`;
    const newMarker = {
      marker_id: markerId,
      question: {
        prompt: formState.prompt.trim(),
        choices: formState.choices.filter((c) => c.text.trim()),
      },
      max_attempts: formState.max_attempts,
    };
    let updated;
    if (editingIdx != null) {
      updated = markers.map((m, i) => (i === editingIdx ? newMarker : m));
    } else {
      updated = [...markers, newMarker];
    }
    updateScene({
      ...scene,
      domain_metadata: { ...meta, content_mcq_markers: updated },
    });
    cancelForm();
  };

  const removeMarker = (idx) => {
    const updated = markers.filter((_, i) => i !== idx);
    updateScene({
      ...scene,
      domain_metadata: { ...meta, content_mcq_markers: updated },
    });
  };

  const updateFormChoice = (choiceIdx, patch) => {
    setFormState((prev) => ({
      ...prev,
      choices: prev.choices.map((c, i) =>
        i === choiceIdx
          ? { ...c, ...patch, ...(patch.correct ? {} : {}) }
          : (patch.correct ? { ...c, correct: false } : c)
      ),
    }));
  };

  const setCorrectChoice = (choiceIdx) => {
    setFormState((prev) => ({
      ...prev,
      choices: prev.choices.map((c, i) => ({ ...c, correct: i === choiceIdx })),
    }));
  };

  const editorCardStyle = {
    background: '#1a1d2e',
    border: '1px solid #2d3748',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginTop: '1rem',
  };
  const editorLabelStyle = {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#64748b',
    marginBottom: '0.5rem',
  };
  const inputStyle = {
    width: '100%',
    background: '#0f1117',
    border: '1px solid #2d3748',
    borderRadius: '4px',
    color: '#e2e8f0',
    padding: '6px 8px',
    fontSize: '0.82rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
  };
  const btnStyle = (primary) => ({
    background: primary ? '#3b82f6' : '#1a1d2e',
    color: primary ? '#fff' : '#94a3b8',
    border: `1px solid ${primary ? '#3b82f6' : '#334155'}`,
    borderRadius: '4px',
    padding: '5px 12px',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
  });

  return (
    <div style={{ marginTop: '1.5rem' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          MCQ Checkpoints ({markers.length})
        </div>
        {!showForm && (
          <button onClick={openAddForm} style={btnStyle(true)}>
            + Add Checkpoint
          </button>
        )}
      </div>

      {/* Existing markers */}
      {markers.map((m, i) => (
        <div key={m.marker_id} style={{ ...editorCardStyle, display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.85rem', color: '#e2e8f0', marginBottom: '0.4rem' }}>
              {i + 1}. {m.question?.prompt || '(no prompt)'}
            </div>
            {(m.question?.choices || []).map((c) => (
              <div key={c.choice_id} style={{
                fontSize: '0.78rem',
                color: c.correct ? '#22c55e' : '#64748b',
                padding: '1px 0',
              }}>
                {c.correct ? '✓' : '○'} {c.text}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button onClick={() => openEditForm(i)} style={btnStyle(false)}>Edit</button>
            <button onClick={() => removeMarker(i)} style={{ ...btnStyle(false), color: '#f87171', borderColor: '#7f1d1d' }}>
              Delete
            </button>
          </div>
        </div>
      ))}

      {/* Add/Edit form */}
      {showForm && formState && (
        <div style={{ ...editorCardStyle, borderColor: '#3b82f6' }}>
          <div style={{ fontWeight: 700, color: '#93c5fd', fontSize: '0.82rem', marginBottom: '0.75rem' }}>
            {editingIdx != null ? 'Edit Checkpoint' : 'New Checkpoint'}
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <div style={editorLabelStyle}>Question prompt</div>
            <textarea
              value={formState.prompt}
              onChange={(e) => setFormState((p) => ({ ...p, prompt: e.target.value }))}
              placeholder="e.g. Which of the following best describes…"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <div style={editorLabelStyle}>Choices (select correct answer)</div>
            {formState.choices.map((c, ci) => (
              <div key={c.choice_id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5 }}>
                <input
                  type="radio"
                  name="correct_choice"
                  checked={c.correct}
                  onChange={() => setCorrectChoice(ci)}
                  style={{ cursor: 'pointer', accentColor: '#22c55e' }}
                  title="Mark as correct"
                />
                <input
                  type="text"
                  value={c.text}
                  onChange={(e) => updateFormChoice(ci, { text: e.target.value })}
                  placeholder={`Choice ${String.fromCharCode(65 + ci)}`}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            ))}
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <div style={editorLabelStyle}>Max attempts</div>
            <input
              type="number"
              min={1}
              max={5}
              value={formState.max_attempts}
              onChange={(e) => setFormState((p) => ({ ...p, max_attempts: Number(e.target.value) || 2 }))}
              style={{ ...inputStyle, width: 60 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={cancelForm} style={btnStyle(false)}>Cancel</button>
            <button
              onClick={saveMarker}
              disabled={!formState.prompt?.trim()}
              style={{ ...btnStyle(true), opacity: formState.prompt?.trim() ? 1 : 0.5 }}
            >
              {editingIdx != null ? 'Save Changes' : 'Add Checkpoint'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContentPreviewCanvas() {
  const scene = useSceneStore((s) => s.scene);
  if (!scene) return null;

  const meta = scene.domain_metadata || {};
  const type = scene.scene_type || '';
  const contentType = (meta.content_type || '').toLowerCase();

  const containerStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#0f1117',
    color: '#e2e8f0',
    minHeight: 0,
    padding: '2rem',
    overflowY: 'auto',
    gap: '1rem',
  };

  const cardStyle = {
    background: '#1a1d2e',
    border: '1px solid #2d3748',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
  };

  const labelStyle = {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#64748b',
    marginBottom: '0.35rem',
  };

  const preStyle = {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    lineHeight: 1.6,
    color: '#94a3b8',
    margin: 0,
  };

  if (type === 'flashcard' || type === 'flashcard_scene') {
    return (
      <FlashcardSceneEditor
        sceneId={scene.scene_id}
        domain={scene.domain_id}
      />
    );
  }

  if (type === 'video' || contentType === 'video') {
    const videoUrl = (meta.video_url || scene.video_url || '').trim();
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {scene.display_name || meta.title || 'Video Scene'}
        </div>
        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
          Type: <strong>video</strong>
          {scene.domain_id ? <> · Domain: <strong>{scene.domain_id}</strong></> : null}
        </div>
        {videoUrl ? (
          <div style={cardStyle}>
            <div style={labelStyle}>Video URL</div>
            <a href={videoUrl} target="_blank" rel="noreferrer" style={{ color: '#93c5fd', wordBreak: 'break-all' }}>
              {videoUrl}
            </a>
            <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: '#64748b' }}>
              Open this scene in the Video Designer for the full player and interaction timeline.
            </div>
          </div>
        ) : (
          <div style={cardStyle}>
            <div style={{ color: '#fbbf24', fontWeight: 600, marginBottom: '0.5rem' }}>
              No video URL yet
            </div>
            <div style={{ fontSize: '0.85rem', color: '#94a3b8', lineHeight: 1.5 }}>
              This scene is tagged as a video but has no playable <code>video_url</code>.
              Use the Video Designer (or paste a YouTube/Loom URL in the inspector) before publishing.
              Transcript / outline fields below are authoring notes, not the video itself.
            </div>
          </div>
        )}
        {(meta.description || scene.description) && (
          <div style={cardStyle}>
            <div style={labelStyle}>Description</div>
            <div style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: 1.5 }}>
              {meta.description || scene.description}
            </div>
          </div>
        )}
        {Array.isArray(meta.transcript_outline) && meta.transcript_outline.length > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>Transcript outline</div>
            <pre style={preStyle}>{JSON.stringify(meta.transcript_outline, null, 2)}</pre>
          </div>
        )}
      </div>
    );
  }

  if (type === 'prompt_canvas') {
    const steps = meta.steps || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Prompt Canvas'}</div>
        {meta.topic_summary && (
          <div style={cardStyle}>
            <div style={labelStyle}>Topic Summary</div>
            <div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{meta.topic_summary}</div>
          </div>
        )}
        {steps.length > 0 && steps.map((step, i) => (
          <div key={i} style={cardStyle}>
            <div style={labelStyle}>Step {i + 1}{step.title ? ` — ${step.title}` : ''}</div>
            {step.prompt && <pre style={preStyle}>{step.prompt}</pre>}
            {step.expected_output && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ ...labelStyle, color: '#22c55e' }}>Expected Output</div>
                <pre style={preStyle}>{step.expected_output}</pre>
              </div>
            )}
          </div>
        ))}
        {meta.key_insight && (
          <div style={{ ...cardStyle, borderColor: '#3b82f6' }}>
            <div style={{ ...labelStyle, color: '#3b82f6' }}>Key Insight</div>
            <div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{meta.key_insight}</div>
          </div>
        )}
        {steps.length === 0 && !meta.topic_summary && (
          <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>
            No content yet — use the editor panel on the right to add steps.
          </div>
        )}
      </div>
    );
  }

  if (type === 'ai_lab') {
    const scenario = meta.scenario || meta.challenge_brief || '';
    const starterPrompt = meta.starter_prompt || meta.starter_code || '';
    const stepsText = meta.steps_and_instructions || '';
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'AI Lab'}</div>
        {scenario && (
          <div style={cardStyle}>
            <div style={labelStyle}>Scenario / Challenge</div>
            <div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{scenario}</div>
          </div>
        )}
        {starterPrompt && (
          <div style={cardStyle}>
            <div style={labelStyle}>Starter Prompt</div>
            <pre style={preStyle}>{starterPrompt}</pre>
          </div>
        )}
        {stepsText && (
          <div style={cardStyle}>
            <div style={labelStyle}>Steps &amp; Instructions</div>
            <pre style={preStyle}>{stepsText}</pre>
          </div>
        )}
        {!scenario && !starterPrompt && !stepsText && (
          <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>
            No content yet — use the AI Lab editor panel on the right.
          </div>
        )}
      </div>
    );
  }

  if (type === 'content_panel' || type === 'reflection') {
    const body = meta.body_markdown
      || meta.content_markdown
      || meta.reading_body
      || meta.script_text
      || '';
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || type}</div>
        {body ? (
          <div style={cardStyle}>
            <div style={labelStyle}>Content</div>
            <pre style={preStyle}>{body}</pre>
          </div>
        ) : (
          <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>
            No content yet — use the Reading body editor on the right.
          </div>
        )}
        {/* ov-4: MCQ checkpoint editor for content_panel scenes */}
        {type === 'content_panel' && (
          <ContentPanelCheckpointEditor sceneId={scene.scene_id} />
        )}
      </div>
    );
  }

  if (type === 'curated_resource') {
    const resourceUrl = meta.url || meta.resource_url || '';
    const isYouTube = /(?:youtube\.com\/(?:watch|embed)|youtu\.be\/)/.test(resourceUrl);
    let embedUrl = resourceUrl;
    if (isYouTube) {
      const m = resourceUrl.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
      );
      if (m) embedUrl = `https://www.youtube.com/embed/${m[1]}`;
    }

    return (
      <div style={{ ...containerStyle, padding: 0 }}>
        {resourceUrl ? (
          <>
            <div style={{ flex: '1 1 70%', minHeight: 0, position: 'relative' }}>
              {isYouTube ? (
                <iframe
                  src={embedUrl}
                  title="YouTube preview"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              ) : (
                <iframe
                  src={resourceUrl}
                  title="Resource preview"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, background: '#fff' }}
                />
              )}
            </div>
            <div style={{ padding: '1rem 1.25rem', background: '#1a1d2e', borderTop: '1px solid #2d3748' }}>
              {meta.source && (
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
                  Source: {meta.source}
                </div>
              )}
              {meta.summary && (
                <div style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5 }}>
                  {meta.summary}
                </div>
              )}
              <div style={{ fontSize: '0.7rem', color: '#475569', marginTop: '0.4rem', wordBreak: 'break-all' }}>
                {resourceUrl}
              </div>
            </div>
          </>
        ) : (
          <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
              No resource URL set — use the editor panel to add one.
            </div>
          </div>
        )}
      </div>
    );
  }

  if (type === 'interactive_quiz' || type === 'quiz_panel') {
    const questions = meta.questions || meta.items || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Interactive Quiz'}</div>
        {questions.length > 0 ? questions.map((q, i) => (
          <div key={i} style={cardStyle}>
            <div style={labelStyle}>
              Question {i + 1}
              {q.concept && <span style={{ marginLeft: 8, color: '#818cf8' }}>({q.concept})</span>}
            </div>
            <div style={{ fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
              {q.prompt || q.question || q.text || '(no prompt)'}
            </div>
            {(q.choices || q.options || []).map((c, ci) => {
              const choiceText = typeof c === 'string' ? c : (c.text || c.label || '');
              const isCorrect = typeof c === 'object'
                ? c.correct || c.is_correct
                : (q.correct_answer === ci || q.correct_index === ci || q.answer === choiceText);
              return (
                <div key={ci} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.35rem 0.6rem', marginBottom: '0.25rem',
                  borderRadius: '4px',
                  background: isCorrect ? 'rgba(34,197,94,0.1)' : 'transparent',
                  border: isCorrect ? '1px solid rgba(34,197,94,0.3)' : '1px solid transparent',
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: isCorrect ? '#22c55e' : '#374151',
                  }} />
                  <span style={{ fontSize: '0.82rem', color: isCorrect ? '#86efac' : '#94a3b8' }}>
                    {choiceText}
                  </span>
                </div>
              );
            })}
            {(q.explanation || q.reasoning) && (
              <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', background: '#111827', borderRadius: '4px' }}>
                <div style={{ ...labelStyle, color: '#f59e0b' }}>Explanation</div>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.5 }}>
                  {q.explanation || q.reasoning}
                </div>
              </div>
            )}
          </div>
        )) : (
          <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>
            No questions yet — use the editor panel on the right to add quiz items.
          </div>
        )}
      </div>
    );
  }

  if (type === 'project' || type === 'capstone') {
    return (
      <ProjectSceneEditorPanel
        scene={scene}
        sceneType={type}
      />
    );
  }

  // Fallback for any other scene type routed through the generic editor
  const entries = Object.entries(meta).filter(([, v]) => v != null && v !== '');
  return (
    <div style={containerStyle}>
      <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || type || 'Scene'}</div>
      <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
        Type: <strong>{type}</strong>
      </div>
      {entries.length > 0 ? (
        entries.map(([key, val]) => (
          <div key={key} style={cardStyle}>
            <div style={labelStyle}>{key.replace(/_/g, ' ')}</div>
            <pre style={preStyle}>{typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val)}</pre>
          </div>
        ))
      ) : (
        <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>
          No domain metadata. Use the inspector on the right to edit scene content.
        </div>
      )}
    </div>
  );
}

// ─── Audience Section — sets audience_tags on a scene ─────────────────────────
// Shows a collapsible panel listing available character classes as checkboxes.
// Only renders when the domain has character_archetypes_enabled = true.
//
// This panel reads the scene from the sceneStore (populated by DesignerShellPage)
// and writes back via sceneAPI.update() + store.setMeta() on save.
function AudienceSection({ sceneId }) {
  const scene = useSceneStore((s) => s.scene);
  const setMeta = useSceneStore((s) => s.setMeta);

  const domainId = scene?.domain_id || '';
  const currentTags = scene?.audience_tags || [];

  const [open, setOpen] = useState(false);
  const [archetypesEnabled, setArchetypesEnabled] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Derive selectedIds from current scene audience_tags + class content_tags
  const computeSelectedIds = (charClasses, audienceTags) => {
    if (!audienceTags || audienceTags.length === 0) return [];
    return charClasses
      .filter(cls => (cls.content_tags || []).some(t => audienceTags.includes(t)))
      .map(cls => cls.class_id || cls.id);
  };

  useEffect(() => {
    if (!domainId) return;
    coachAPI.listCharacterClasses(domainId)
      .then(({ data }) => {
        const enabled = Boolean(data.character_archetypes_enabled);
        setArchetypesEnabled(enabled);
        if (enabled) {
          const charClasses = data.character_classes || [];
          setClasses(charClasses);
          setSelectedIds(computeSelectedIds(charClasses, currentTags));
        }
      })
      .catch(() => setLoadError('Could not load character classes.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  // Keep selectedIds in sync if scene changes externally
  useEffect(() => {
    if (classes.length > 0) {
      setSelectedIds(computeSelectedIds(classes, currentTags));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentTags)]);

  if (!archetypesEnabled || !domainId) return null;

  const toggleClass = (classId) => {
    setSelectedIds(prev =>
      prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]
    );
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // audience_tags = union of content_tags from all selected classes
      const audienceTags = [...new Set(
        classes
          .filter(cls => selectedIds.includes(cls.class_id || cls.id))
          .flatMap(cls => cls.content_tags || [])
      )];
      await sceneAPI.update(sceneId, { audience_tags: audienceTags });
      setMeta({ audience_tags: audienceTags });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const panelStyle = {
    background: '#0f172a',
    borderBottom: '1px solid #1e293b',
    flexShrink: 0,
  };
  const headerStyle = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '6px 16px', cursor: 'pointer', userSelect: 'none',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#8b5cf6' };
  const mutedStyle = { fontSize: 11, color: '#64748b', flex: 1 };
  const chevron = open ? '▾' : '▸';

  return (
    <div style={panelStyle}>
      {/* Collapsible header */}
      <div style={headerStyle} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: 11, color: '#64748b' }}>{chevron}</span>
        <span style={labelStyle}>🎭 Audience</span>
        {currentTags.length > 0 ? (
          <span style={mutedStyle}>
            {selectedIds.length > 0
              ? selectedIds.map(id => classes.find(c => (c.class_id || c.id) === id)?.label || id).join(', ')
              : `${currentTags.length} tag${currentTags.length !== 1 ? 's' : ''} set`}
          </span>
        ) : (
          <span style={mutedStyle}>All learners — click to restrict by role</span>
        )}
        {saved && <span style={{ fontSize: 11, color: '#22c55e' }}>✓ Saved</span>}
      </div>

      {/* Expanded panel */}
      {open && (
        <div style={{ padding: '0 16px 12px', borderTop: '1px solid #1e293b' }}>
          <div style={{ paddingTop: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', marginBottom: 2 }}>
              Who is this scene for?
            </div>
            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5, marginBottom: 10 }}>
              Leave all unchecked to show to all learners regardless of their learning path.
              Select roles to route this scene only to matching archetypes.
            </div>
          </div>

          {loadError && (
            <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>⚠ {loadError}</div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {classes.map(cls => {
              const id = cls.class_id || cls.id;
              const checked = selectedIds.includes(id);
              return (
                <label
                  key={id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    padding: '4px 10px', borderRadius: 6, fontSize: 12,
                    border: `1px solid ${checked ? '#8b5cf6' : '#334155'}`,
                    background: checked ? 'rgba(139,92,246,0.12)' : 'transparent',
                    color: checked ? '#c4b5fd' : '#94a3b8',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleClass(id)}
                    style={{ accentColor: '#8b5cf6' }}
                  />
                  {cls.icon && <span>{cls.icon}</span>}
                  {cls.label || id}
                </label>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: '#4f46e5', color: '#fff', border: 'none',
                borderRadius: 5, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Save Audience'}
            </button>
            {currentTags.length > 0 && (
              <button
                onClick={async () => {
                  setSaving(true);
                  try {
                    await sceneAPI.update(sceneId, { audience_tags: [] });
                    setMeta({ audience_tags: [] });
                    setSelectedIds([]);
                  } finally { setSaving(false); }
                }}
                disabled={saving}
                style={{
                  background: 'transparent', color: '#64748b',
                  border: '1px solid #334155', borderRadius: 5,
                  padding: '5px 12px', fontSize: 12, cursor: 'pointer',
                }}
              >
                Clear (show to all)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Variant Content Panel — per-archetype content overrides ──────────────────
// Shows a collapsible panel with tab navigation per character class.
// Only renders when the domain has character_archetypes_enabled = true.
// Fields shown depend on scene content_type from metadata.
function VariantContentPanel({ sceneId }) {
  const scene = useSceneStore((s) => s.scene);
  const setMeta = useSceneStore((s) => s.setMeta);

  const domainId = scene?.domain_id || '';
  const contentType = scene?.metadata?.content_type || scene?.scene_type || '';

  // Load initial variant data from scene
  const initialVariants = scene?.character_variants || scene?.scene_data?.character_variants || {};

  const [open, setOpen] = useState(false);
  const [archetypesEnabled, setArchetypesEnabled] = useState(false);
  const [classes, setClasses] = useState([]);
  const [variantData, setVariantData] = useState(initialVariants);
  const [activeTab, setActiveTab] = useState(null);
  const [variantSaving, setVariantSaving] = useState(false);
  const [variantSaved, setVariantSaved] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Determine which fields to show based on content_type
  const FIELDS_BY_TYPE = {
    ai_lab:       ['challenge_brief', 'starter_prompt', 'success_criteria'],
    capstone:     ['challenge_brief', 'starter_prompt', 'success_criteria'],
    code_lab:     ['problem_context', 'starter_code', 'hint'],
    prompt_canvas:['instructions', 'focus_prompt', 'example_output'],
    reflection:   ['prompt', 'example_response'],
  };
  const fields = FIELDS_BY_TYPE[contentType] || ['instructions', 'description'];

  useEffect(() => {
    if (!domainId) return;
    coachAPI.listCharacterClasses(domainId)
      .then(({ data }) => {
        const enabled = Boolean(data.character_archetypes_enabled);
        setArchetypesEnabled(enabled);
        if (enabled) {
          const charClasses = data.character_classes || [];
          setClasses(charClasses);
          if (charClasses.length > 0 && !activeTab) {
            setActiveTab(charClasses[0].class_id || charClasses[0].id);
          }
        }
      })
      .catch(() => setLoadError('Could not load character classes.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  // Sync variantData if scene changes externally
  useEffect(() => {
    const incoming = scene?.character_variants || scene?.scene_data?.character_variants || {};
    setVariantData(incoming);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.character_variants]);

  if (!archetypesEnabled || !domainId) return null;

  const handleFieldChange = (classId, fieldName, value) => {
    setVariantData(prev => ({
      ...prev,
      [classId]: { ...(prev[classId] || {}), [fieldName]: value },
    }));
    setVariantSaved(false);
  };

  const handleSave = async () => {
    setVariantSaving(true);
    try {
      await sceneAPI.update(sceneId, { character_variants: variantData });
      setMeta({ character_variants: variantData });
      setVariantSaved(true);
      setTimeout(() => setVariantSaved(false), 2500);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setVariantSaving(false);
    }
  };

  const panelStyle = {
    background: '#0f172a',
    borderBottom: '1px solid #1e293b',
    flexShrink: 0,
  };
  const headerStyle = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '6px 16px', cursor: 'pointer', userSelect: 'none',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#06b6d4' };
  const mutedStyle = { fontSize: 11, color: '#64748b', flex: 1 };
  const chevron = open ? '▾' : '▸';

  const inputStyle = {
    width: '100%',
    background: '#0f1117',
    border: '1px solid #2d3748',
    borderRadius: '4px',
    color: '#e2e8f0',
    padding: '6px 8px',
    fontSize: '0.82rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
  };
  const fieldLabelStyle = {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#64748b',
    marginBottom: '0.35rem',
  };

  const populatedClassCount = Object.keys(variantData).filter(id =>
    fields.some(f => variantData[id]?.[f]?.trim())
  ).length;

  return (
    <div style={panelStyle}>
      {/* Collapsible header */}
      <div style={headerStyle} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: 11, color: '#64748b' }}>{chevron}</span>
        <span style={labelStyle}>🧬 Variant Content</span>
        <span style={mutedStyle}>
          {populatedClassCount > 0
            ? `${populatedClassCount} archetype${populatedClassCount !== 1 ? 's' : ''} customised`
            : 'Per-archetype content overrides'}
        </span>
        {variantSaved && <span style={{ fontSize: 11, color: '#22c55e' }}>✓ Saved</span>}
      </div>

      {/* Expanded panel */}
      {open && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid #1e293b' }}>
          <div style={{ paddingTop: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', marginBottom: 2 }}>
              Set per-archetype content overrides.
            </div>
            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
              Only shown when character archetypes are enabled for this domain.
              Fields left blank will fall back to the default scene content.
            </div>
          </div>

          {loadError && (
            <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>⚠ {loadError}</div>
          )}

          {classes.length === 0 && !loadError && (
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
              No character classes found for this domain.
            </div>
          )}

          {classes.length > 0 && (
            <>
              {/* Tab / pill navigation */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                {classes.map(cls => {
                  const id = cls.class_id || cls.id;
                  const isActive = activeTab === id;
                  const hasContent = fields.some(f => variantData[id]?.[f]?.trim());
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveTab(id)}
                      style={{
                        background: isActive ? 'rgba(6,182,212,0.15)' : 'transparent',
                        color: isActive ? '#67e8f9' : '#94a3b8',
                        border: `1px solid ${isActive ? '#06b6d4' : '#334155'}`,
                        borderRadius: 20,
                        padding: '3px 12px',
                        fontSize: 12,
                        fontWeight: isActive ? 700 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {cls.icon && <span style={{ marginRight: 4 }}>{cls.icon}</span>}
                      {cls.label || id}
                      {hasContent && (
                        <span style={{ marginLeft: 5, fontSize: 10, color: '#22c55e' }}>●</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Fields for active tab */}
              {activeTab && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {fields.map(field => (
                    <div key={field}>
                      <div style={fieldLabelStyle}>{field.replace(/_/g, ' ')}</div>
                      <textarea
                        rows={field === 'starter_code' || field === 'starter_prompt' ? 4 : 3}
                        value={variantData[activeTab]?.[field] || ''}
                        onChange={e => handleFieldChange(activeTab, field, e.target.value)}
                        placeholder={`Override ${field.replace(/_/g, ' ')} for this archetype…`}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Save button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button
                  onClick={handleSave}
                  disabled={variantSaving}
                  style={{
                    background: '#0e7490', color: '#fff', border: 'none',
                    borderRadius: 5, padding: '5px 16px', fontSize: 12, fontWeight: 600,
                    cursor: variantSaving ? 'not-allowed' : 'pointer',
                    opacity: variantSaving ? 0.7 : 1,
                  }}
                >
                  {variantSaving ? 'Saving…' : 'Save Variants'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Project / Capstone Scene Editor Panel ────────────────────────────────────
// Full-featured authoring panel for `project` and `capstone` scene types.
// Reads initial state from scene.domain_metadata via sceneStore, maintains a
// local draft, and saves explicitly via sceneAPI.update() + setMeta().
//
// Rendered in ContentPreviewCanvas's canvas slot so coaches get the full
// form in place of the read-only preview that other scene types use.
function ProjectSceneEditorPanel({ scene, sceneType }) {
  const setMeta = useSceneStore((s) => s.setMeta);

  const buildInitialDefaults = (type, existingMeta) => {
    const meta = existingMeta || {};
    const defaults = {
      brief_md: meta.brief_md || '',
      deliverables: meta.deliverables || [],
      rubric: meta.rubric || { criteria: [] },
      grading_config: {
        review_levels: type === 'capstone' ? 2 : 1,
        min_pass_score: 70,
        max_submissions: 3,
        gates_certificate: type === 'capstone',
        gates_module: false,
        notify_on_ai_grade: true,
        notify_on_coach_review: false,
        ...(meta.grading_config || {}),
      },
    };
    return defaults;
  };

  const [formData, setFormData] = useState(() =>
    buildInitialDefaults(sceneType, scene?.domain_metadata)
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Re-sync form when the scene document changes externally (e.g. version restore)
  useEffect(() => {
    if (scene?.domain_metadata) {
      setFormData(buildInitialDefaults(sceneType, scene.domain_metadata));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.scene_id]);

  const handleSave = async () => {
    if (!scene?.scene_id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await sceneAPI.update(scene.scene_id, { domain_metadata: formData });
      setMeta({ domain_metadata: formData });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveError(err?.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    background: '#0f172a',
    borderBottom: '1px solid #1e293b',
    flexShrink: 0,
  };
  const saveBtnStyle = {
    background: saving ? '#334155' : '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 5,
    padding: '6px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: saving ? 'not-allowed' : 'pointer',
    opacity: saving ? 0.7 : 1,
    transition: 'background 0.15s',
  };

  const typeLabel = sceneType === 'capstone' ? 'Capstone Project' : 'Project Submission';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={headerStyle}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{typeLabel}</span>
          {sceneType === 'capstone' && (
            <span style={{ marginLeft: 8, fontSize: 11, color: '#a78bfa', background: 'rgba(167,139,250,0.12)', padding: '2px 7px', borderRadius: 10, border: '1px solid rgba(167,139,250,0.3)' }}>
              Certificate-gating
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved && (
            <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>✓ Saved</span>
          )}
          {saveError && (
            <span style={{ fontSize: 12, color: '#f87171' }}>⚠ {saveError}</span>
          )}
          <button onClick={handleSave} disabled={saving} style={saveBtnStyle}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: '#0f1117' }}>
        <ProjectSceneForm
          sceneType={sceneType}
          value={formData}
          onChange={setFormData}
        />
      </div>
    </div>
  );
}

export default function GenericSceneEditorPage() {
  const { sceneId } = useParams();
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);

  const versionBtnStyle = {
    background: '#1e293b',
    color: '#94a3b8',
    border: '1px solid #334155',
    borderRadius: 6,
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };

  return (
    <div style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '6px 16px',
        background: '#0f172a',
        borderBottom: '1px solid #1e293b',
        flexShrink: 0,
      }}>
        <button style={versionBtnStyle} onClick={() => setVersionHistoryOpen(true)}>
          Version History
        </button>
      </div>
      {/* Audience panel — only renders when character_archetypes_enabled for the domain */}
      <AudienceSection sceneId={sceneId} />
      {/* Variant Content panel — per-archetype content overrides (P2) */}
      <VariantContentPanel sceneId={sceneId} />
      <div style={{ flex: 1, minHeight: 0 }}>
        <DesignerShellPage
          sceneId={sceneId || null}
          canvasSlot={<ContentPreviewCanvas />}
          userRoles={[]}
          appSurface="coach"
        />
      </div>
      <VersionHistoryPanel
        sceneId={sceneId}
        isOpen={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        onRestore={() => window.location.reload()}
      />
    </div>
  );
}
